import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";

import FilterListItem from "@client/components/modules/FilterList/Item";
import FilterListWrapper from "@client/components/modules/FilterList/Wrapper";
import ListTemplate from "@client/components/templates/ListTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import PaginationControls from "@client/components/ui/PaginationControls";
import ExportPdfButton from "@client/utils/ExportPdfButton";
import licenceImage from "@client/components/ui/sharedImages/licence.svg";
import customerStore from "@client/stores/CustomerStore";
import licenceStore from "@client/stores/LicenceStore";
import tagStore from "@client/stores/TagStore";
import Style from "@client/utils/Style";
import {
  isSapLicenceVersion,
  Licence,
  LICENCE_VERSION_LABELS,
  LICENCE_VERSION_SAP,
  normalizeLicenceVersion,
} from "@typings/Licence";

import LicenceModal from "../LicenceModal";

const Wrapper = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ExportButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-grow: 1;
`;

type State = {
  showLicenceModal: boolean;
  selectedLicences: Licence[];
  showDeleteAlert: boolean;
  currentPage: number;
  itemsPerPage: number;
};
type Props = {
  onNavigate: (path: string) => void;
};
@observer
class LicencesPage extends React.Component<Props, State> {
  state: State = {
    showLicenceModal: false,
    selectedLicences: [],
    showDeleteAlert: false,
    currentPage: 1,
    itemsPerPage: 25,
  };

  componentDidMount() {
    this.loadLicences();
    licenceStore.load();
    tagStore.load();
  }

  loadLicences() {
    const { currentPage, itemsPerPage } = this.state;
    licenceStore.load({
      page: currentPage,
      limit: itemsPerPage,
      forceShowLoading: true,
    });
  }

  handleItemClick(licence: Licence) {
    this.props.onNavigate(`/licences/${licence.id}`);
  }

  handleReloadButtonClick() {
    this.loadLicences();
    customerStore.load();
    tagStore.load();
  }

  handlePageChange = (newPage: number) => {
    this.setState({ currentPage: newPage }, () => {
      this.loadLicences();
    });
  };

  handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      { itemsPerPage: parseInt(event.target.value, 10), currentPage: 1 },
      () => {
        this.loadLicences();
      },
    );
  };

  itemFilterFunction(
    licence: Licence,
    filterItem?: string | null,
    filterText?: string,
  ) {
    filterText = (filterText || "").toLowerCase();
    filterItem = filterItem || "";

    const searchGroup =
      filterItem === "all" || (licence.tag && licence.tag === filterItem);

    const searchText =
      licence.id.toString().indexOf(filterText) > -1 ||
      (licence.name && licence.name.toLowerCase().indexOf(filterText) > -1) ||
      (licence.tag && licence.tag.toLowerCase().indexOf(filterText) > -1) ||
      licence.created_by.toLowerCase().indexOf(filterText) > -1;

    if (searchGroup && searchText) {
      return true;
    }

    return false;
  }

  getListColumns(licence: Licence) {
    let validity;
    if (licence.type === "fixed") {
      validity = (
        <div>
          <div>{moment.utc(licence.start_date).format("DD MMM YYYY")}</div>
          <div>{moment.utc(licence.end_date).format("DD MMM YYYY")}</div>
        </div>
      );
    } else {
      validity = (
        <div>
          <div>{`${licence.duration || "-"} days`}</div>
          <div>{moment.utc(licence.end_date).format("DD MMM YYYY")}</div>
        </div>
      );
    }

    return [
      {
        name: "Duration",
        value: validity,
        style: { minWidth: "144px", maxWidth: "144px" },
      },
      {
        name: "Migrations / Replicas",
        value: `${licence.migrations} Migrations / ${licence.replicas} Replicas`,
        style: { minWidth: "204px", maxWidth: "204px" },
      },
      {
        name: "Created at",
        value: moment(licence.created_at).format("DD MMM YYYY HH:mm"),
        style: { minWidth: "144px", maxWidth: "144px" },
      },
      {
        name: "Created by",
        value: `${licence.created_by}`,
        style: { minWidth: "144px", maxWidth: "144px" },
      },
    ];
  }

  getFilterItems(): { label: string; value: string }[] {
    const tags = [
      ...new Set(licenceStore.licences.map(l => l.tag).filter(Boolean)),
    ].map(tname => ({
      label: tname as string,
      value: tname as string,
    }));
    return [{ label: "All", value: "all" }, ...tags];
  }

  handleDeleteBulkLicences() {
    this.setState({ showDeleteAlert: false });
    this.state.selectedLicences.forEach(licence => {
      licenceStore.delete(licence.id);
    });
  }

  getMigrationsReplicas(licence: Licence) {
    return `${licence.migrations} Migrations / ${licence.replicas} Replicas`;
  }

  render() {
    const { currentPage, itemsPerPage } = this.state;
    const licences = licenceStore.licences.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );

    const columns = [
      { header: "No.", key: "index" },
      { header: "Customer", key: "name" },
      { header: "Tag", key: "tag" },
      { header: "License Type", key: "licence_type" },
      { header: "Duration", key: "duration" },
      { header: "Migrations / Replicas", key: "migrations_replicas" },
      { header: "Created by", key: "created_by" },
      { header: "Created at", key: "created_at" },
    ];

    const dataWithIndex = licences.map((licence, index) => {
      let duration;
      if (licence.type === "fixed") {
        duration = `${moment.utc(licence.start_date).format("DD MMM YYYY")} - ${moment.utc(licence.end_date).format("DD MMM YYYY")}`;
      } else {
        duration = `${licence.duration || "-"} days - ${moment.utc(licence.end_date).format("DD MMM YYYY")}`;
      }

      return {
        ...licence,
        index: (currentPage - 1) * itemsPerPage + index + 1,
        created_at: moment(licence.created_at).format("DD MMM YYYY HH:mm"),
        migrations_replicas: this.getMigrationsReplicas(licence),
        licence_type:
          LICENCE_VERSION_LABELS[normalizeLicenceVersion(licence.version)],
        duration: duration,
      };
    });

    const totalPages = Math.ceil(licenceStore.licences.length / itemsPerPage);

    return (
      <Wrapper>
        <ListTemplate
          title="Coriolis Licences"
          listComponent={
            <FilterListWrapper
              filterItems={this.getFilterItems()}
              onSelectedItemsChange={selectedLicences => {
                this.setState({ selectedLicences });
              }}
              selectionLabel="licences"
              loading={licenceStore.loading}
              items={licences}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              itemFilterFunction={(...args) => this.itemFilterFunction(...args)}
              renderItemComponent={options => {
                let name = options.item.name;
                if (!name) {
                  const customer = customerStore.customers.find(
                    o => o.id === options.item.customer_id,
                  );
                  if (!customer) {
                    name = "Loading...";
                  } else {
                    name = customer.name;
                  }
                }

                const tag = tagStore.tags.find(
                  t => t.name === options.item.tag,
                );
                const tagColor = tag ? tag.color : "#777A8B";
                return (
                  <FilterListItem
                    {...options}
                    image={licenceImage}
                    title={name}
                    columns={this.getListColumns(options.item)}
                    pillLabel={tagStore.loading ? "" : options.item.tag}
                    pillBackgroundColor={tagColor}
                    extraPills={
                      isSapLicenceVersion(options.item.version)
                        ? [
                            {
                              label:
                                LICENCE_VERSION_LABELS[LICENCE_VERSION_SAP],
                              backgroundColor: Style.palette.success,
                              fontColor: "white",
                              borderColor: Style.palette.success,
                            },
                          ]
                        : null
                    }
                  />
                );
              }}
              emptyListImage={licenceImage}
              emptyListMessage="You don't have any created licences"
              emptyListExtraMessage="The created licences will appear here"
              emptyListButtonLabel="New Licence"
              onEmptyListButtonClick={() => {
                this.setState({ showLicenceModal: true });
              }}
            />
          }
          footerText={licences.length ? `${licences.length} licences` : ""}
          footerComponent={
            <>
              <PaginationWrapper>
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  onPageChange={this.handlePageChange}
                  onItemsPerPageChange={this.handleItemsPerPageChange}
                />
              </PaginationWrapper>
              <ExportButtonWrapper>
                <ExportPdfButton
                  data={dataWithIndex}
                  title="Licences"
                  columns={columns}
                />
              </ExportButtonWrapper>
            </>
          }
        />
        {this.state.showLicenceModal ? (
          <LicenceModal
            onRequestClose={() => {
              this.setState({ showLicenceModal: false });
            }}
          />
        ) : null}
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Licences?"
            message="Are you sure you want to delete the selected licences?"
            extraMessage="Deleting a Coriolis Licence is permanent!"
            onConfirmation={() => {
              this.handleDeleteBulkLicences();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function LicencesPageWithNavigate() {
  const navigate = useNavigate();
  return <LicencesPage onNavigate={navigate} />;
}

export default LicencesPageWithNavigate;
